import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, Dumbbell, Utensils, Moon, Brain, Timer, Flame, Target, ArrowRight, Check } from 'lucide-react';
import { setProgressForSlug } from '../../pages/GuidesHubPage';
import RelatedGuides from './RelatedGuides';

const ARTICLE_SLUG = 'how-to-train-like-an-athlete';

const NAV = [
  { id: 'foundation', label: 'Foundation' },
  { id: 'plan', label: 'Weekly Plan' },
  { id: 'home', label: 'Home Training' },
  { id: 'routine', label: 'Daily Routine' },
  { id: 'nutrition', label: 'Nutrition' },
  { id: 'faq', label: 'FAQ' },
];

const PILLARS = [
  { icon: Target, title: 'Structured Plans', desc: 'Progressive overload with planned intensity, volume, and complexity increases.', color: '#3b82f6' },
  { icon: Timer, title: 'Periodization', desc: 'Training divided into phases: strength, power, competition peaking.', color: '#E63946' },
  { icon: Dumbbell, title: 'Strength & Conditioning', desc: 'Compound lifts, core stability, flexibility, and balance work.', color: '#f59e0b' },
  { icon: Utensils, title: 'Nutrition & Recovery', desc: 'Balanced macros, sleep priority, active recovery protocols.', color: '#22c55e' },
];

const WEEKLY_PLAN = [
  { day: 'Monday', focus: 'Upper Strength', exercises: ['Bench Press 4x6', 'Barbell Row 4x6', 'OHP 3x8', 'Pull-ups 3xMax', 'Face Pulls 3x15'], intensity: 'High', color: '#E63946' },
  { day: 'Tuesday', focus: 'Lower Power', exercises: ['Back Squat 5x5', 'Romanian DL 4x8', 'Box Jumps 4x5', 'Walking Lunges 3x12', 'Calf Raises 4x15'], intensity: 'High', color: '#3b82f6' },
  { day: 'Wednesday', focus: 'Active Recovery', exercises: ['30 min Zone 2 Cardio', 'Foam Rolling 15 min', 'Mobility Work 15 min', 'Light Stretching'], intensity: 'Low', color: '#22c55e' },
  { day: 'Thursday', focus: 'Upper Hypertrophy', exercises: ['Incline DB Press 4x10', 'Cable Rows 4x12', 'Lateral Raises 3x15', 'Bicep Curls 3x12', 'Tricep Pushdowns 3x12'], intensity: 'Medium', color: '#f59e0b' },
  { day: 'Friday', focus: 'Lower Strength', exercises: ['Deadlift 4x5', 'Front Squat 3x8', 'Leg Press 3x12', 'Hamstring Curls 3x12', 'Core Circuit x3'], intensity: 'High', color: '#E63946' },
  { day: 'Saturday', focus: 'Conditioning', exercises: ['HIIT Circuit 30 min', 'Sled Push/Pull', 'Battle Ropes', 'Box Jumps', 'Farmer Carries'], intensity: 'High', color: '#8b5cf6' },
  { day: 'Sunday', focus: 'Full Rest', exercises: ['Sleep 8+ hours', 'Light walk', 'Meal prep', 'Mental visualization'], intensity: 'Rest', color: '#6b7280' },
];

const HOME_EQUIPMENT = [
  { name: 'Dumbbells', essential: true },
  { name: 'Resistance Bands', essential: true },
  { name: 'Jump Rope', essential: true },
  { name: 'Stability Ball', essential: false },
  { name: 'Pull-up Bar', essential: false },
  { name: 'Kettlebell', essential: false },
];

const DAILY_BLOCKS = [
  { time: '6:00 AM', label: 'Morning Training', icon: Dumbbell, desc: 'Strength or conditioning session', color: '#E63946' },
  { time: '8:00 AM', label: 'Nutrition', icon: Utensils, desc: 'High-protein breakfast, hydration', color: '#22c55e' },
  { time: '12:00 PM', label: 'Midday Fuel', icon: Flame, desc: 'Balanced macro meal, supplements', color: '#f59e0b' },
  { time: '3:00 PM', label: 'Active Recovery', icon: Timer, desc: 'Foam rolling, stretching, mobility', color: '#3b82f6' },
  { time: '6:00 PM', label: 'Evening Training', icon: Target, desc: 'Sport-specific drills or skill work', color: '#8b5cf6' },
  { time: '9:00 PM', label: 'Mental Prep & Sleep', icon: Moon, desc: 'Visualization, 8+ hours sleep', color: '#6b7280' },
];

const MACROS = [
  { name: 'Protein', pct: 30, sources: 'Lean meats, fish, eggs, dairy, beans, lentils', color: '#E63946', tip: 'Crucial for muscle repair and growth. Aim for 1g per lb of body weight.' },
  { name: 'Carbs', pct: 45, sources: 'Whole grains, fruits, vegetables, sweet potatoes', color: '#3b82f6', tip: 'Primary fuel source. Time complex carbs around training sessions.' },
  { name: 'Fats', pct: 25, sources: 'Avocados, nuts, seeds, olive oil, fatty fish', color: '#f59e0b', tip: 'Important for hormone production and energy. Don\'t go below 20%.' },
];

const FAQS = [
  { q: 'How long does it take to train like an athlete?', a: 'Expect 3-6 months of consistent training to see significant improvements. Athletes train for years to reach elite levels, but you\'ll notice strength and conditioning gains within the first month.' },
  { q: 'Can I train like an athlete at home?', a: 'Absolutely. With dumbbells, resistance bands, and bodyweight exercises, you can build a solid foundation. Add outdoor training (parks, hills, stairs) for conditioning variety.' },
  { q: 'How many days per week should I train?', a: '5-6 days with 1-2 rest/active recovery days. Quality matters more than quantity — a well-structured 4-day program beats 7 days of random workouts.' },
  { q: 'What supplements do athletes take?', a: 'Foundation: creatine monohydrate, protein powder, vitamin D, omega-3s. Beyond that, it depends on your sport and goals. Get nutrition right before relying on supplements.' },
];

export default function TrainLikeAthleteArticle() {
  const [activeNav, setActiveNav] = useState('foundation');
  const [selectedDay, setSelectedDay] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);
  const [openMacro, setOpenMacro] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const pct = docHeight > 0 ? Math.min(100, Math.round((scrollTop / docHeight) * 100)) : 0;
      setProgressForSlug(ARTICLE_SLUG, pct);

      const sections = NAV.map(n => document.getElementById(n.id));
      for (let i = sections.length - 1; i >= 0; i--) {
        if (sections[i] && sections[i].getBoundingClientRect().top <= 150) {
          setActiveNav(NAV[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const day = WEEKLY_PLAN[selectedDay];

  return (
    <div ref={containerRef} className="min-h-screen bg-[#0a0a0a] text-zinc-300">
      {/* Sticky Nav */}
      <div className="sticky top-[60px] z-40 bg-[#0a0a0a]/95 backdrop-blur-md border-b border-zinc-800/50">
        <div className="max-w-4xl mx-auto px-4 flex gap-1 overflow-x-auto py-2 scrollbar-hide">
          {NAV.map(n => (
            <button key={n.id} onClick={() => document.getElementById(n.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className={`px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider whitespace-nowrap transition-all ${activeNav === n.id ? 'bg-[#3b82f6] text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
              data-testid={`nav-${n.id}`}>{n.label}</button>
          ))}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 pt-8 pb-20">
        {/* Hero */}
        <div className="mb-12">
          <span className="text-[#3b82f6] text-[10px] uppercase tracking-widest font-heading">Fitness / Performance</span>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mt-3 mb-4 leading-tight">
            How to Train Like an Athlete
          </h1>
          <p className="text-zinc-400 text-base max-w-xl">A comprehensive guide to structured training, periodization, and the daily habits that separate athletes from casual gym-goers.</p>
          <div className="flex items-center gap-4 mt-4 text-xs text-zinc-600 font-mono">
            <span>10 min read</span>
            <span>Interactive weekly plan</span>
          </div>
        </div>

        {/* Section: Foundation */}
        <section id="foundation" className="mb-16 scroll-mt-28" data-testid="section-foundation">
          <h2 className="font-heading text-2xl text-white mb-6">The 4 Pillars of Athletic Training</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {PILLARS.map(p => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="bg-zinc-900/60 border border-zinc-800 p-4 hover:border-zinc-700 transition-colors" data-testid={`pillar-${p.title.toLowerCase().replace(/\s/g, '-')}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-7 h-7 rounded flex items-center justify-center" style={{ backgroundColor: `${p.color}15`, color: p.color }}>
                      <Icon className="w-3.5 h-3.5" />
                    </span>
                    <span className="text-white text-sm font-bold">{p.title}</span>
                  </div>
                  <p className="text-zinc-500 text-xs leading-relaxed">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Section: Weekly Plan */}
        <section id="plan" className="mb-16 scroll-mt-28" data-testid="section-plan">
          <h2 className="font-heading text-2xl text-white mb-2">Interactive Weekly Training Plan</h2>
          <p className="text-zinc-500 text-sm mb-6">Click a day to see the full workout breakdown.</p>

          <div className="flex gap-1 mb-4 overflow-x-auto scrollbar-hide">
            {WEEKLY_PLAN.map((d, i) => (
              <button key={d.day} onClick={() => setSelectedDay(i)}
                className={`px-3 py-2 text-[10px] font-heading uppercase tracking-wider whitespace-nowrap border transition-all ${selectedDay === i ? 'border-zinc-600 bg-zinc-800 text-white' : 'border-zinc-800/50 text-zinc-600 hover:text-zinc-400'}`}
                data-testid={`day-${d.day.toLowerCase()}`}>
                {d.day.slice(0, 3)}
              </button>
            ))}
          </div>

          <div className="bg-zinc-900/60 border border-zinc-800 p-5" data-testid="day-detail">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-white font-heading text-lg">{day.day}</p>
                <p className="text-sm" style={{ color: day.color }}>{day.focus}</p>
              </div>
              // eslint-disable-next-line no-nested-ternary -- readable tier picker
              <span className={`text-[10px] font-heading uppercase tracking-wider px-2 py-0.5 border ${day.intensity === 'High' ? 'border-red-800/50 text-red-400 bg-red-950/20' : day.intensity === 'Medium' ? 'border-yellow-800/50 text-yellow-400 bg-yellow-950/20' : day.intensity === 'Low' ? 'border-green-800/50 text-green-400 bg-green-950/20' : 'border-zinc-800 text-zinc-500'}`}>
                {day.intensity}
              </span>
            </div>
            <ul className="space-y-2">
              {day.exercises.map((e, i) => (
                <li key={e.name} className="flex items-center gap-2 text-sm text-zinc-400">
                  <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-mono bg-zinc-800 text-zinc-500">{i + 1}</span>
                  {e}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Section: Home Training */}
        <section id="home" className="mb-16 scroll-mt-28" data-testid="section-home">
          <h2 className="font-heading text-2xl text-white mb-4">Train at Home Like an Athlete</h2>
          <p className="text-zinc-400 leading-relaxed mb-6">You don't need a fancy gym. With some creativity and the right resources, you can create a challenging and effective workout environment at home.</p>

          <div className="mb-6">
            <p className="text-zinc-600 text-[10px] uppercase tracking-wider font-heading mb-3">Equipment Checklist</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {HOME_EQUIPMENT.map(e => (
                <div key={e.name} className="flex items-center gap-2 bg-zinc-900/40 border border-zinc-800/50 p-3">
                  <Check className={`w-3.5 h-3.5 ${e.essential ? 'text-green-500' : 'text-zinc-600'}`} />
                  <span className="text-sm text-zinc-400">{e.name}</span>
                  {e.essential && <span className="ml-auto text-[8px] text-green-600 font-mono uppercase">essential</span>}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-blue-950/20 border border-blue-900/20 p-4">
            <p className="text-blue-400 text-xs font-heading uppercase tracking-wider mb-2">Home Workout Ideas</p>
            <ul className="text-zinc-400 text-sm space-y-1">
              <li>- <strong className="text-zinc-300">Bodyweight:</strong> Push-ups, squats, lunges, planks, burpees</li>
              <li>- <strong className="text-zinc-300">Outdoor:</strong> Parks, stairs, and hills for conditioning</li>
              <li>- <strong className="text-zinc-300">Circuits:</strong> 30-sec work / 15-sec rest, 4-5 exercises, 4 rounds</li>
            </ul>
          </div>
        </section>

        {/* Section: Daily Routine */}
        <section id="routine" className="mb-16 scroll-mt-28" data-testid="section-routine">
          <h2 className="font-heading text-2xl text-white mb-2">The Athlete's Daily Routine</h2>
          <p className="text-zinc-500 text-sm mb-6">A structured day optimized for performance and recovery.</p>
          <div className="space-y-2">
            {DAILY_BLOCKS.map((b, i) => {
              const Icon = b.icon;
              return (
                <div key={b.label} className="flex items-center gap-4 bg-zinc-900/30 border border-zinc-800/50 p-3 hover:border-zinc-700 transition-colors" data-testid={`routine-${b.time.replace(/[:\s]/g,'-').toLowerCase()}`}>
                  <span className="text-zinc-600 text-xs font-mono w-16 flex-shrink-0">{b.time}</span>
                  <span className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${b.color}12`, color: b.color }}>
                    <Icon className="w-4 h-4" />
                  </span>
                  <div>
                    <p className="text-white text-sm font-bold">{b.label}</p>
                    <p className="text-zinc-500 text-xs">{b.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Section: Nutrition */}
        <section id="nutrition" className="mb-16 scroll-mt-28" data-testid="section-nutrition">
          <h2 className="font-heading text-2xl text-white mb-6">Athlete Nutrition Breakdown</h2>

          {/* Macro bar */}
          <div className="flex h-4 mb-4 overflow-hidden">
            {MACROS.map(m => <div key={m.name} className="h-full" style={{ width: `${m.pct}%`, backgroundColor: m.color }} />)}
          </div>
          <div className="flex gap-4 mb-6">
            {MACROS.map(m => (
              <span key={m.name} className="flex items-center gap-1.5 text-xs text-zinc-500">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: m.color }} /> {m.name} {m.pct}%
              </span>
            ))}
          </div>

          <div className="space-y-2">
            {MACROS.map((m, i) => (
              <div key={m.name} className={`border transition-all ${openMacro === i ? 'border-zinc-700 bg-zinc-900/60' : 'border-zinc-800/50'}`}>
                <button onClick={() => setOpenMacro(openMacro === i ? null : i)}
                  className="w-full flex items-center justify-between p-4 text-left" data-testid={`macro-${m.name.toLowerCase()}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-1 rounded" style={{ backgroundColor: m.color }} />
                    <span className="text-white text-sm font-bold">{m.name}</span>
                    <span className="text-zinc-600 text-xs font-mono">{m.pct}%</span>
                  </div>
                  {openMacro === i ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
                </button>
                {openMacro === i && (
                  <div className="px-4 pb-4">
                    <p className="text-zinc-400 text-sm mb-2">{m.tip}</p>
                    <p className="text-zinc-600 text-xs"><strong className="text-zinc-400">Best sources:</strong> {m.sources}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-4 bg-green-950/20 border border-green-900/20 p-4">
            <p className="text-green-400 text-xs font-heading uppercase tracking-wider mb-2">Hydration</p>
            <p className="text-zinc-400 text-sm">Drink at least half your body weight (lbs) in ounces of water daily. Add electrolytes during and after training sessions.</p>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="mb-16 scroll-mt-28" data-testid="section-faq">
          <h2 className="font-heading text-2xl text-white mb-6">Frequently Asked Questions</h2>
          <div className="space-y-2">
            {FAQS.map((f, i) => (
              <div key={f.q} className={`border transition-all ${openFaq === i ? 'border-zinc-700 bg-zinc-900/60' : 'border-zinc-800/50'}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-4 text-left" data-testid={`faq-${i}`}>
                  <span className="text-white text-sm font-bold pr-4">{f.q}</span>
                  {openFaq === i ? <ChevronUp className="w-4 h-4 text-zinc-500 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" />}
                </button>
                {openFaq === i && <p className="px-4 pb-4 text-zinc-400 text-sm leading-relaxed">{f.a}</p>}
              </div>
            ))}
          </div>
        </section>

        <RelatedGuides currentSlug={ARTICLE_SLUG} />

        <div className="bg-amber-950/20 border border-amber-700/30 p-4 text-xs text-amber-200/70 leading-relaxed">
          <strong className="text-amber-200">Disclaimer:</strong> This training plan is for informational purposes only. Consult with a healthcare provider before starting any new exercise program. Adjust intensity based on your fitness level and any pre-existing conditions.
        </div>
      </div>
    </div>
  );
}
